import React, { useEffect, useState } from "react";
import axios from 'axios';
import Select from "react-select";
import { useConfig } from "../utils/ConfigContext";
import { createAxiosInstance } from "../utils/AxiosInstance";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";

const CenterGroup = () => {
    const { API_BASE_URL } = useConfig();
    const AxiosInstance = createAxiosInstance(API_BASE_URL);

    const [centerOptions, setCenterOptions] = useState([]);
    const [groupName, setGroupName] = useState("");
    const [description, setDescription] = useState("");
    const [selectedCenters, setSelectedCenters] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [groupsList, setGroupsList] = useState([]);
    const [listLoading, setListLoading] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [viewingGroup, setViewingGroup] = useState(null);

    //get user detail
    const userData = useSelector((state) => state.auth.userData);
    const myCenters = userData?.hospitals;

    const fetchCenterGroups = async () => {
        setListLoading(true);
        try {
            const res = await AxiosInstance.get('/centergroups');
            const groups = res.data?.data || [];
            setGroupsList(groups);
        } catch (err) {
            console.error('Error fetching center groups:', err);
            setError("Failed to fetch center groups");
        } finally {
            setListLoading(false);
        }
    }

    const handleDelete = async (groupId) => {
        if (window.confirm("Are you sure you want to delete this group?")) {
            try {
                await AxiosInstance.delete(`/centergroups/${groupId}`);
                setSuccess("Group deleted successfully!");
                fetchCenterGroups();
                setTimeout(() => setSuccess(""), 3000);
            } catch (err) {
                const errorMessage = err.response?.data?.message || "Failed to delete group";
                setError(errorMessage);
                console.error("Error deleting group:", err);
                setTimeout(() => setError(""), 3000);
            }
        }
    }

    const handleEdit = (group) => {
        setEditingId(group.id);
        setGroupName(group.name);
        setDescription(group.description);
        // Handle selected centers mapping if available
        if (group.centers) {
            const centers = Array.isArray(group.centers)
                ? group.centers.map(c => ({
                    value: c.hm_id || c,
                    label: c.hm_name || c
                }))
                : [];
            setSelectedCenters(centers);
        }
    }

    const handleView = (group) => {
        setViewingGroup(group);
        setShowViewModal(true);
    }

    const handleCloseViewModal = () => {
        setShowViewModal(false);
        setViewingGroup(null);
    }

    const handleCancel = () => {
        setEditingId(null);
        setGroupName("");
        setDescription("");
        setSelectedCenters([]);
        setError("");
    }

    const handleSubmit = async (event) => {
        event.preventDefault();

        // Validation
        if (!groupName.trim()) {
            setError("Group Name is required");
            return;
        }

        if (selectedCenters.length === 0) {
            setError("Please select at least one center");
            return;
        }

        setLoading(true);
        setError("");
        setSuccess("");

        try {
            const payload = {
                group_name: groupName,
                description: description,
                centers: selectedCenters.map(center => center.value),
                group_id: editingId ?? null
            };


            let response;
            response = await AxiosInstance.post('/centergroups', payload);

            if (response.status === 200 || response.status === 201) {
                setSuccess(editingId ? "Group updated successfully!" : "Group created successfully!");
                setGroupName("");
                setDescription("");
                setSelectedCenters([]);
                setEditingId(null);
                fetchCenterGroups();

                // Clear success message after 3 seconds
                setTimeout(() => setSuccess(""), 3000);
            }
        } catch (err) {
            const errorMessage = err.response?.data?.message || "Failed to save group. Please try again.";
            setError(errorMessage);
            console.error("Error saving group:", err);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        setCenterOptions(myCenters.map(center => ({
            value: center.hm_id,
            label: center.hm_name
        })));

        fetchCenterGroups();
    }, []);

    return (

        <div className="row">
            {(error || success) && (
                <div className="col-12">
                    <div
                        className={`alert ${error ? "alert-danger" : "alert-success"
                            } alert-dismissible fade show`}
                        role="alert"
                    >
                        {error || success}
                        <button
                            type="button"
                            className="btn-close"
                            data-bs-dismiss="alert"
                            aria-label="Close"
                        ></button>
                    </div>
                </div>
            )}
            <div className="col-md-6">
                <div className="card">
                    <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center">
                        <h6 className="mb-4 text-xl">Groups List</h6>
                        <Link to="/center-group-overview" className="text-primary text-decoration-underline"  >Group Overview</Link>
                        </div>

                        {listLoading ? (
                            <div className="text-center p-3">Loading...</div>
                        ) : groupsList.length === 0 ? (
                            <div className="text-center text-neutral-500 p-3">No groups found</div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-hover">
                                    <thead>
                                        <tr>
                                            <th>Group Name</th>
                                            <th>Description</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {groupsList.map((group) => (
                                            <tr key={group.id}>
                                                <td>{group.name}</td>
                                                <td>{group.description || "-"}</td>
                                                <td>
                                                    <button
                                                        className="btn btn-sm btn-primary me-2"
                                                        onClick={() => handleView(group)}
                                                    >
                                                        View
                                                    </button>
                                                    <button
                                                        className="btn btn-sm btn-warning me-2"
                                                        onClick={() => handleEdit(group)}
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        className="btn btn-sm btn-danger"
                                                        onClick={() => handleDelete(group.id)}
                                                    >
                                                        Delete
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                    </div>
                </div>
            </div>
            <div className="col-md-6">
                <div className="card">
                    <div className="card-body">
                        <p className="text-neutral-500">
                            {editingId ? "Edit group details." : "Fill up group details."}
                        </p>

                        <div className="form-wizard">
                            <form>
                                <div className="row gy-3">
                                    <div className="col-sm-6">
                                        <label className="form-label">Group Name*</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Enter Group Name"
                                            value={groupName}
                                            onChange={(e) => setGroupName(e.target.value)}
                                            required
                                        />
                                    </div>

                                    <div className="col-sm-6">
                                        <label className="form-label">Description</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Enter Description"
                                            value={description}
                                            onChange={(e) => setDescription(e.target.value)}
                                        />
                                    </div>

                                    <div className="col-12">
                                        <label className="form-label">Centers*</label>
                                        <Select
                                            options={centerOptions}
                                            isMulti
                                            placeholder="Select centers"
                                            value={selectedCenters}
                                            onChange={(selected) => setSelectedCenters(selected || [])}
                                        />
                                    </div>

                                    <div className="text-end mt-3">
                                        <button
                                            onClick={handleSubmit}
                                            type="submit"
                                            className="btn btn-primary-600 px-32"
                                            disabled={loading}
                                        >
                                            {loading ? "Submitting..." : editingId ? "Update" : "Submit"}
                                        </button>
                                        {editingId && (
                                            <button
                                                onClick={handleCancel}
                                                type="button"
                                                className="btn btn-secondary px-32 ms-2"
                                                disabled={loading}
                                            >
                                                Cancel
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </form>
                        </div>

                    </div>
                </div>
            </div>

            {/* View Modal */}
            {showViewModal && viewingGroup && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">{viewingGroup.name}</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={handleCloseViewModal}
                                ></button>
                            </div>
                            <div className="modal-body">

                                <div>
                                    <label className="fw-bold">Centers:</label>
                                    {viewingGroup.centers && viewingGroup.centers.length > 0 ? (
                                        <ul className="list-group mt-2">
                                            {viewingGroup.centers.map((center, index) => (
                                                <li key={index} className="list-group-item">
                                                    {center.hm_name || center}
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <p className="text-muted">No centers assigned</p>
                                    )}
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={handleCloseViewModal}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>

    );
};

export default CenterGroup;
